import { useEffect, useRef } from 'react';

export interface OrderConfirmationData {
  purchaseCode?: string;
  status?: string;
  [key: string]: unknown;
}

interface UseOrderConfirmationSSEOptions {
  /** Purchase code of the order to listen for. Connection is skipped when empty. */
  purchaseCode?: string | null;
  /** Gate the connection (e.g. only while awaiting a bank-transfer payment). */
  enabled?: boolean;
  /** Called once when this order's payment is confirmed. */
  onConfirmed: (data: OrderConfirmationData) => void;
}

const MAX_RECONNECT_DELAY = 30_000; // cap backoff at 30s
const MAX_LIFETIME = 15 * 60 * 1000; // stop listening after 15 min

/**
 * Subscribes to the storefront order-confirmation SSE stream
 * (GET {VITE_BASEURL}store/order-notifications?purchaseCode=...) and invokes `onConfirmed`
 * when an "OrderPaymentConfirmation" event for this purchase code arrives.
 *
 * Wire format mirrors the backend NotificationEvent: each message is
 * { eventName, data } where `data` is a JSON string of the order payload.
 */
export function useOrderConfirmationSSE({
  purchaseCode,
  enabled = true,
  onConfirmed,
}: UseOrderConfirmationSSEOptions): void {
  // Keep the latest callback without re-opening the connection on every render.
  const onConfirmedRef = useRef(onConfirmed);
  onConfirmedRef.current = onConfirmed;

  useEffect(() => {
    if (!enabled || !purchaseCode) return;

    const baseUrl = import.meta.env.VITE_BASEURL as string;
    const url = `${baseUrl}store/order-notifications?purchaseCode=${encodeURIComponent(
      purchaseCode
    )}`;

    let cancelled = false;
    let confirmed = false;
    let attempt = 0;
    let source: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const closeSource = () => {
      if (source) {
        source.close();
        source = null;
      }
    };

    const connect = () => {
      if (cancelled || confirmed) return;

      source = new EventSource(url);

      source.onopen = () => {
        attempt = 0;
      };

      source.onmessage = (event: MessageEvent<string>) => {
        let parsed: { eventName?: string; data?: unknown };
        try {
          parsed = JSON.parse(event.data);
        } catch {
          return;
        }

        if (!parsed.eventName || parsed.eventName === 'ping') return;

        let payload = parsed.data as OrderConfirmationData | string;
        if (typeof payload === 'string') {
          try {
            payload = JSON.parse(payload);
          } catch {
            return;
          }
        }
        const data = payload as OrderConfirmationData;

        if (
          parsed.eventName === 'OrderPaymentConfirmation' &&
          data?.purchaseCode === purchaseCode
        ) {
          confirmed = true;
          closeSource();
          onConfirmedRef.current(data);
        }
      };

      source.onerror = () => {
        // Take over reconnection so a 404/closed stream doesn't hammer the server.
        closeSource();
        if (cancelled || confirmed) return;
        attempt += 1;
        const delay = Math.min(1000 * 2 ** attempt, MAX_RECONNECT_DELAY);
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();

    const lifetimeTimer = setTimeout(() => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      closeSource();
    }, MAX_LIFETIME);

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      clearTimeout(lifetimeTimer);
      closeSource();
    };
  }, [purchaseCode, enabled]);
}

export default useOrderConfirmationSSE;
